import { getMedia } from '@/infrastructure/storage'
import { aliyunMusicService } from '@/features/editor/services/aliyun-music-service'
import { mediaLibraryService } from '@/features/media-library/services/media-library-service'
import type { MediaMetadata } from '@/types/storage'

export interface GenerateBackgroundMusicRequest {
  topic: string
  tone: 'natural' | 'professional' | 'friendly' | 'energetic' | 'calm'
  durationSeconds: number
  onProgress?: (stage: string, fraction?: number) => void
  signal?: AbortSignal
}

function buildMusicPrompt(request: GenerateBackgroundMusicRequest): string {
  const toneMap: Record<GenerateBackgroundMusicRequest['tone'], string> = {
    natural: 'natural, light, clean',
    professional: 'professional, modern, polished',
    friendly: 'friendly, warm, upbeat',
    energetic: 'energetic, punchy, motivating',
    calm: 'calm, soft, relaxed',
  }

  const trimmedTopic = request.topic.trim() || 'product introduction'
  return `Instrumental background music for a short video voiceover about ${trimmedTopic}, ${toneMap[request.tone]}, no vocals, suitable under narration.`
}

export async function generateBackgroundMusic(request: GenerateBackgroundMusicRequest): Promise<{
  blob: Blob
  file: File
  duration: number
  prompt: string
  model: 'fun-music-v1'
}> {
  const prompt = buildMusicPrompt(request)
  const result = await aliyunMusicService.generateMusicFile({
    prompt,
    isInstrumental: true,
    onProgress: (stage) => request.onProgress?.(stage),
  })

  return {
    ...result,
    prompt,
    model: 'fun-music-v1',
  }
}

export async function loadBackgroundMusicFromMedia(mediaId: string): Promise<{
  media: MediaMetadata
  blob: Blob
}> {
  const media = await getMedia(mediaId)
  if (!media) {
    throw new Error('未找到所选背景音乐素材。')
  }
  if (!media.mimeType.startsWith('audio/')) {
    throw new Error('所选素材不是音频，无法作为背景音乐使用。')
  }

  const blob = await mediaLibraryService.getMediaFile(mediaId)
  if (!blob) {
    throw new Error('无法读取所选背景音乐文件。')
  }

  return { media, blob }
}
